import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import UserService from '../../services/userService';
import RoleService from '../../services/roleService';
import Button from '../common/Button';
import { formatDateEs } from '../../utils';
import './UserDetails.css';

const UserDetails = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const auth = useAuth();
    const [user, setUser] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditingRole, setIsEditingRole] = useState(false);
    const [isSavingRole, setIsSavingRole] = useState(false);
    const [orgRoles, setOrgRoles] = useState([]);
    const [roleIndex, setRoleIndex] = useState('0');
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isReactivating, setIsReactivating] = useState(false);

    const fetchAllData = useCallback(async () => {
        try {
            setIsLoading(true);
            const token = auth.user?.access_token;
            const currentUserId = auth.user?.profile?.sub;

            if (!token || !currentUserId) {
                setError('No user authentication available');
                return;
            }

            const [targetUser, currentUserData, rolesResponse] = await Promise.all([
                UserService.getUserById(userId, token),
                UserService.getUserById(currentUserId, token),
                RoleService.getAllRoles(token)
            ]);

            const loadedRoles = rolesResponse.data || [];
            setOrgRoles(loadedRoles);
            setUser(targetUser);
            setCurrentUser(currentUserData);

            // Compute initial roleIndex from user's current appRole / customRoleId
            const uAppRole = targetUser.appRole || 'user';
            const uCustomRoleId = targetUser.customRoleId || null;
            const custom = loadedRoles.map(r => ({ appRole: 'user', customRoleId: r.id }));
            if (custom.length === 0) custom.push({ appRole: 'user', customRoleId: null });
            const opts = [...custom, { appRole: 'admin', customRoleId: null }, { appRole: 'owner', customRoleId: null }];
            let initIndex = 0;
            if (uAppRole === 'user' && uCustomRoleId) {
                const idx = opts.findIndex(o => o.appRole === 'user' && o.customRoleId === uCustomRoleId);
                if (idx !== -1) initIndex = idx;
            } else {
                const idx = opts.findIndex(o => o.appRole === uAppRole && !o.customRoleId);
                if (idx !== -1) initIndex = idx;
            }
            setRoleIndex(String(initIndex));

        } catch (err) {
            console.error('Error fetching data:', err);
            setError(err.message || 'Error fetching user details');
        } finally {
            setIsLoading(false);
        }
    }, [auth.user, userId]);

    useEffect(() => {
        if (auth.isAuthenticated) {
            fetchAllData();
        }
    }, [auth.isAuthenticated, fetchAllData]);

    const roleOptions = useMemo(() => {
        const custom = orgRoles.map(r => ({ label: r.name, appRole: 'user', customRoleId: r.id }));
        if (custom.length === 0) custom.push({ label: 'Usuario', appRole: 'user', customRoleId: null });
        return [
            ...custom,
            { label: 'Administrador', appRole: 'admin', customRoleId: null },
            { label: 'Propietario', appRole: 'owner', customRoleId: null }
        ];
    }, [orgRoles]);

    const getRoleDisplayName = (appRole, customRoleId) => {
        if (appRole === 'user' && customRoleId) {
            const found = orgRoles.find(r => r.id === customRoleId);
            return found ? found.name : 'Usuario';
        }
        switch (appRole) {
            case 'owner': return 'Propietario';
            case 'admin': return 'Administrador';
            default: return 'Usuario';
        }
    };

    const canModifyUser = () => {
        if (!currentUser || !user) return false;
        if (user.id === currentUser.id) return false;
        if (currentUser.appRole === 'owner') return true;
        if (currentUser.appRole === 'admin' && user.appRole !== 'owner') return true;
        return false;
    };

    const canRemoveUser = () => {
        if (!currentUser || !user) return false;
        if (user.id === currentUser.id) return false;
        if (currentUser.appRole === 'owner') return true;
        if (currentUser.appRole === 'admin' && user.appRole !== 'owner') return true;
        return false;
    };

    const handleRoleUpdate = async () => {
        try {
            setIsSavingRole(true);
            const selected = roleOptions[parseInt(roleIndex, 10)] || roleOptions[0];
            const token = auth.user?.access_token;
            await UserService.updateUserRole(userId, selected.appRole, token, selected.customRoleId);
            setMessage({ type: 'success', text: 'Rol actualizado correctamente' });
            setIsEditingRole(false);
            fetchAllData();
        } catch (err) {
            setMessage({ type: 'error', text: err.message || 'Error al actualizar rol' });
        } finally {
            setIsSavingRole(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!window.confirm(`¿Estás seguro de que deseas desactivar a ${user.name}? El usuario no podrá acceder a la aplicación pero podrás reactivarlo más tarde.`)) return;

        try {
            const token = auth.user?.access_token;
            await UserService.deactivateUser(userId, token);
            navigate('/settings/users');
        } catch (err) {
            setMessage({ type: 'error', text: err.message || 'Error al desactivar usuario' });
        }
    };

    const handleReactivateUser = async () => {
        if (!window.confirm(`¿Deseas reactivar a ${user.name}? El usuario volverá a ocupar un lugar en tu plan.`)) return;

        try {
            setIsReactivating(true);
            const token = auth.user?.access_token;
            await UserService.activateUser(userId, token);
            setMessage({ type: 'success', text: 'Usuario reactivado correctamente' });
            fetchAllData();
        } catch (err) {
            setMessage({ type: 'error', text: err.message || 'Error al reactivar usuario' });
        } finally {
            setIsReactivating(false);
        }
    };

    const handleBack = () => {
        navigate('/settings/users');
    };

    if (isLoading) {
        return (
            <div className="user-details-container loading">
                <div className="spinner"></div>
            </div>
        );
    }
    
    // ... Error and Not Found states remain roughly the same, will be refreshed by full file rewrite if needed or kept partial.
    // Simplifying replace by providing full render content below in next step or keeping partial if I use replace_string.
    // I shall rewrite the render part completely. 


    if (error) {
        return (
            <div className="user-details-container error">
                <h2>Error</h2>
                <p>{error}</p>
                <Button onClick={handleBack}>Volver</Button>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="user-details-container not-found">
                <h2>Usuario no encontrado</h2>
                <Button onClick={handleBack}>Volver</Button>
            </div>
        );
    }

    return (
        <div className="user-details-container">
            <div className="user-details-header-nav">
                <Button onClick={handleBack} variant="secondary" className="back-btn">
                    <i className="fas fa-arrow-left"></i> Volver a Usuarios
                </Button>
            </div>

            {message.text && (
                <div className={`user-message ${message.type}`}>
                    <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                    <span>{message.text}</span>
                    <button onClick={() => setMessage({ type: '', text: '' })} className="close-message">
                        <i className="fas fa-times"></i>
                    </button>
                </div>
            )}

            <div className="user-profile-header">
                <div className="user-avatar-placeholder">
                    {user.name.charAt(0).toUpperCase()}
                </div>
                <div className="user-profile-info">
                    <h1>{user.name}</h1>
                    <p className="user-email">{user.email}</p>
                    <div className="user-badges">
                        <span className={`role-badge ${user.appRole || 'user'}`}>
                            {getRoleDisplayName(user.appRole, user.customRoleId)}
                        </span>
                        {user.id === currentUser?.id && <span className="me-badge">Yo</span>}
                        {user.active === false && <span className="inactive-badge">Inactivo</span>}
                    </div>
                </div>
            </div>

            <div className="user-details-grid">
                <div className="detail-card info-card">
                    <div className="detail-header">
                        <h2><i className="fas fa-user-circle"></i> Información Personal</h2>
                    </div>
                    <div className="detail-body">
                        <div className="detail-item">
                            <label>Nombre Completo</label>
                            <div className="value">{user.name}</div>
                        </div>
                        <div className="detail-item">
                            <label>Email</label>
                            <div className="value">{user.email}</div>
                        </div>
                        <div className="detail-item">
                            <label>Fecha de Registro</label>
                            <div className="value">
                                {user.createdAt ? formatDateEs(user.createdAt) : 'Desconocida'}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="detail-card role-card">
                    <div className="detail-header">
                        <h2><i className="fas fa-id-badge"></i> Rol y Permisos</h2>
                    </div>
                    <div className="detail-body">
                        <div className="detail-item">
                            <label>Rol Actual</label>
                            {isEditingRole ? (
                                <div className="role-editor">
                                    <select
                                        value={roleIndex}
                                        onChange={(e) => setRoleIndex(e.target.value)}
                                        className="role-select-large"
                                    >
                                        {roleOptions.map((opt, idx) => (
                                            <option key={idx} value={String(idx)}>{opt.label}</option>
                                        ))}
                                    </select>
                                    <div className="role-actions">
                                        <Button onClick={handleRoleUpdate} className="btn-save-sm" disabled={isSavingRole}>
                                            {isSavingRole ? <><i className="fas fa-spinner fa-spin"></i> Guardando...</> : 'Guardar'}
                                        </Button>
                                        <Button onClick={() => setIsEditingRole(false)} variant="secondary" className="btn-cancel-sm" disabled={isSavingRole}>Cancelar</Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="role-display-large">
                                    <span className="role-text">{getRoleDisplayName(user.appRole, user.customRoleId)}</span>
                                    {canModifyUser() && (
                                        <button onClick={() => setIsEditingRole(true)} className="btn-icon-text">
                                            <i className="fas fa-pen"></i> Cambiar
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="role-description">
                            <div className="role-info-section">
                                <h4>Roles por Defecto</h4>
                                <ul className="role-info-list">
                                    <li><strong>Propietario:</strong> Acceso Total</li>
                                    <li><strong>Administrador:</strong> Acceso Total, pero no puede remover o editar propietarios.</li>
                                </ul>
                            </div>
                            {orgRoles.length > 0 && (
                                <div className="role-info-section">
                                    <h4>Roles Personalizados</h4>
                                    <ul className="role-info-list">
                                        {orgRoles.map(role => (
                                            <li key={role.id}>{role.name}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <p>
                                <button onClick={() => navigate('/settings/roles')} className="btn-link">
                                    Gestionar roles
                                </button>
                            </p>
                        </div>
                    </div>
                </div>

                {canRemoveUser() && user.active === false && (
                    <div className="detail-card reactivate-card">
                        <div className="detail-header reactivate-header">
                            <h2><i className="fas fa-user-check"></i> Usuario Inactivo</h2>
                        </div>
                        <div className="detail-body">
                            <p>Este usuario está desactivado y no puede acceder a la aplicación. Al reactivarlo volverá a ocupar un lugar en tu plan.</p>
                            <button onClick={handleReactivateUser} className="btn-reactivate-large" disabled={isReactivating}>
                                {isReactivating
                                    ? <><i className="fas fa-spinner fa-spin"></i> Reactivando...</>
                                    : <><i className="fas fa-user-check"></i> Reactivar Usuario</>}
                            </button>
                        </div>
                    </div>
                )}

                {canRemoveUser() && user.active !== false && (
                    <div className="detail-card danger-card">
                         <div className="detail-header danger-header">
                            <h2><i className="fas fa-exclamation-triangle"></i> Zona de Peligro</h2>
                        </div>
                        <div className="detail-body">
                            <p>Puedes desactivar usuarios para liberarlos de tu plan. Podrás reactivarlos cuando lo necesites.</p>
                            <button onClick={handleDeleteUser} className="btn-danger-large">
                                <i className="fas fa-user-slash"></i> Desactivar Usuario
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserDetails;
